/**
 * Blog listing: auto-load articles 10-at-a-time via Section Rendering API
 * when the user scrolls near the end of the list.
 */
class BlogPostsLoadMore extends HTMLElement {
  /** @type {boolean} */
  #loading = false;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.sentinel = this.querySelector('[data-blog-load-more-sentinel]');
    this.grid = this.querySelector('[data-blog-load-more-grid]');
    this.status = this.querySelector('[data-blog-load-more-status]');

    if (!this.grid) return;

    if (this.currentPage >= this.lastPage) {
      this.#hideSentinel();
      return;
    }

    if (!this.sentinel || !('IntersectionObserver' in window)) {
      // Fallback: load remaining pages sequentially if observer is unavailable
      this.#loadUntilComplete();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#loadMore();
          }
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0,
      }
    );

    this.#observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get sectionId() {
    return this.getAttribute('section-id') || '';
  }

  get currentPage() {
    return Number(this.dataset.page || '1');
  }

  get lastPage() {
    return Number(this.dataset.lastPage || '1');
  }

  /**
   * @param {number} page
   */
  #setPage(page) {
    this.dataset.page = String(page);
  }

  #hideSentinel() {
    if (this.sentinel) {
      this.sentinel.hidden = true;
    }
    this.#observer?.disconnect();
  }

  #setLoading(isLoading) {
    this.#loading = isLoading;
    if (this.sentinel) {
      this.sentinel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    if (this.status) {
      this.status.textContent = isLoading ? this.dataset.loadingLabel || '' : '';
    }
  }

  async #loadUntilComplete() {
    while (this.currentPage < this.lastPage) {
      const loaded = await this.#loadMore();
      if (!loaded) break;
    }
  }

  /**
   * @returns {Promise<boolean>} whether a page was appended
   */
  async #loadMore() {
    if (this.#loading || !this.sectionId) return false;

    const nextPage = this.currentPage + 1;
    if (nextPage > this.lastPage) {
      this.#hideSentinel();
      return false;
    }

    this.#setLoading(true);

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(nextPage));
      url.searchParams.set('section_id', this.sectionId);
      url.hash = '';

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Failed to load page ${nextPage}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = doc.querySelectorAll('[data-blog-load-more-item]');

      if (!items.length) {
        this.#hideSentinel();
        return false;
      }

      const fragment = document.createDocumentFragment();
      items.forEach((item) => {
        fragment.appendChild(document.importNode(item, true));
      });
      this.grid.appendChild(fragment);

      this.#setPage(nextPage);

      if (nextPage >= this.lastPage) {
        this.#hideSentinel();
      }

      return true;
    } catch (error) {
      console.warn('Blog posts infinite load failed:', error);
      return false;
    } finally {
      this.#setLoading(false);
    }
  }
}

if (!customElements.get('blog-posts-load-more')) {
  customElements.define('blog-posts-load-more', BlogPostsLoadMore);
}
